import * as constantClass from '../components/fileWithConstants';

export const MD_PAGE_SIZE = 25;
export const LOOKUP_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 450;

export function buildIndexUrl(baseUrl, page, pageSize, search, extraParams = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (search != null && String(search).trim() !== '') {
    params.set('search', String(search).trim());
  }
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value != null && value !== '') {
      params.set(key, String(value));
    }
  });
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${params.toString()}`;
}

export async function loadIndexPage(baseUrl, page, pageSize, search, extraParams) {
  const url = buildIndexUrl(baseUrl, page, pageSize, search, extraParams);
  const { data } = await constantClass.apiFetch(url, { method: 'GET' });
  return data;
}

export function parseIndexPageResponse(data, fallbackForbidden = 'Forbidden.') {
  if (data === 'Timeout') {
    return { type: 'timeout' };
  }
  if (data === 'Forbidden.' || data?.errorMessage) {
    return { type: 'error', message: data?.errorMessage || fallbackForbidden };
  }
  const model = data?.returnModel;
  if (!model || !Array.isArray(model.items)) {
    return { type: 'error', message: 'Respuesta de listado inválida.' };
  }
  return {
    type: 'ok',
    items: model.items,
    totalCount: model.totalCount || 0,
    page: model.page ?? 0,
    pageSize: model.pageSize ?? 0,
  };
}

export function toIndexPageState(page, perPage, parsed) {
  return {
    paginationData: parsed.items,
    pageCount: Math.ceil(parsed.totalCount / perPage) || 0,
    totalCount: parsed.totalCount,
    offset: page * perPage,
    currentPage: page,
    loading: false,
    searching: false,
    showErrorMessage: false,
    errorMessage: '',
  };
}

export function isIndexInitialLoading(state) {
  return Boolean(state.loading) && (!state.paginationData || state.paginationData.length === 0);
}

export function scheduleIndexSearch(component, callback) {
  if (component._indexSearchTimer) {
    clearTimeout(component._indexSearchTimer);
  }
  component._indexSearchTimer = setTimeout(callback, SEARCH_DEBOUNCE_MS);
}

export function handleIndexSearchChange(component, event, onSearch) {
  const value = event.target.value;
  component.setState({ searchInput: value });
  scheduleIndexSearch(component, () => onSearch(value));
}

function shouldUseBackgroundLoad(component, options) {
  if (options.background === true) {
    return true;
  }
  if (options.background === false) {
    return false;
  }
  return Array.isArray(component.state.paginationData) && component.state.paginationData.length > 0;
}

export async function fetchAndApplyIndexPage(component, {
  indexUrl,
  page = component.state.currentPage || 0,
  search = component.state.searchInput || '',
  perPage = component.state.perPage,
  extraParams = {},
  background,
  onTimeout = () => constantClass.RedirectToLogin(),
}) {
  const requestId = (component._indexRequestId || 0) + 1;
  component._indexRequestId = requestId;

  const useBackground = shouldUseBackgroundLoad(component, { background });
  if (useBackground) {
    component.setState({ searching: true });
  } else {
    component.setState({ loading: true, searching: false });
  }

  const data = await loadIndexPage(indexUrl, page, perPage, search, extraParams);
  if (requestId !== component._indexRequestId) {
    if (component.state.searching) {
      component.setState({ searching: false });
    }
    return null;
  }

  if (data === 'Timeout') {
    onTimeout();
    return null;
  }
  const parsed = parseIndexPageResponse(data);
  if (parsed.type === 'timeout') {
    onTimeout();
    return null;
  }
  if (parsed.type === 'error') {
    component.setState({
      showErrorMessage: true,
      errorMessage: parsed.message,
      loading: false,
      searching: false,
    });
    return null;
  }
  component.setState({
    ...toIndexPageState(page, perPage, parsed),
    searchInput: typeof search === 'string' ? search.trim() : '',
    currentPage: page,
  });
  return parsed;
}
